import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import Tooltip, { TooltipProps } from '../tooltip/tooltip';
import { CssUnitsAsResponsiveObject } from '../../utils/get-css-rule-with-responsive-calc';

export interface EllipsisWithTooltipProps {
  children?: React.ReactNode;
  className?: string;
  tooltipProps?: TooltipProps;
  hideTooltip?: boolean;
  forceTooltip?: boolean;
  lines?: number;
  width?: number;
  maxWidth?: number | string | CssUnitsAsResponsiveObject<React.CSSProperties['maxWidth']>;
}

function EllipsisWithTooltipComponent({
  children,
  className,
  tooltipProps,
  hideTooltip = false,
  forceTooltip = false,
  lines,
}: EllipsisWithTooltipProps) {
  const [hasOverflowingChildren, setHasOverflowingChildren] = React.useState(false);
  const [text, setText] = React.useState<string>('');

  React.useEffect(() => {
    // Reset the overflow state when children change
    setHasOverflowingChildren(false);
  }, [children]);

  const updateOverflow = (event: React.MouseEvent<HTMLSpanElement>) => {
    const el = event.target as HTMLSpanElement;
    if (el.scrollWidth > el.clientWidth || (lines && el.scrollHeight > el.clientHeight)) {
      setHasOverflowingChildren(true);
      setText(el.textContent || '');
    } else {
      setHasOverflowingChildren(false);
    }
  };

  const contentClassName = classNames('box-ellipsis', className);

  return hasOverflowingChildren || forceTooltip ? (
    <Tooltip title={text} disabled={hideTooltip} {...tooltipProps}>
      <span className={contentClassName} onMouseEnter={updateOverflow}>
        {children}
      </span>
    </Tooltip>
  ) : (
    <span className={contentClassName} onMouseEnter={updateOverflow}>
      {children}
    </span>
  );
}

const EllipsisWithTooltip = styled(EllipsisWithTooltipComponent)<EllipsisWithTooltipProps>`
  width: ${props => (typeof props.width === 'number' ? `${props.width}px` : '100%')};
  display: block;

  max-width: ${props => (typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : '100%')};

  &.inline-block {
    display: inline-block;
    width: auto;
  }

  &,
  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @supports (-webkit-line-clamp: 2) {
      ${({ lines }) =>
        lines &&
        css`
          -webkit-box-orient: vertical;
          -webkit-line-clamp: ${lines};
          display: -webkit-box;
          white-space: pre-line;
          word-wrap: break-word;
        `}
    }
  }
`;

export const WidthContainer = styled.div<{ width?: number; maxWidth?: number }>`
  width: ${props => (typeof props.width === 'number' ? `${props.width}px` : props.width)};
  max-width: ${props => `${props.maxWidth ? `${props.maxWidth}px` : ''}`};
`;

export default EllipsisWithTooltip;
